import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GlDecoratedSurveyAnswer } from "utils/v3/surveyUtils";

interface SurveyState {
  [key: string]: any;
  surveyAnswersForAllPreviousSurveys?: Array<{
    name: string;
    value: string | boolean;
    questionAsAsked?: any;
  }>;
}

const initialState: SurveyState = {};

export const SurveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {
    setSurveyDataForSpecificSurvey(
      state,
      { payload }: PayloadAction<{ surveyIdentifier: string; surveyData: any }>
    ) {
      if (!state[payload.surveyIdentifier]) {
        state[payload.surveyIdentifier] = {};
      }
      state[payload.surveyIdentifier]["data"] = payload.surveyData;
    },
    updateSurveyAnswersForAllPreviousSurveys(
      state,
      {
        payload
      }: PayloadAction<{
        data?: {
          previousAnswers?: GlDecoratedSurveyAnswer[];
          surveyId?: string;
          structure?: any;
        };
      }>
    ) {
      const previousAnswers = payload?.data?.previousAnswers || [];

      // If there are no previous answers and no existing answers, initialize empty array
      if (!state.surveyAnswersForAllPreviousSurveys) {
        state.surveyAnswersForAllPreviousSurveys = [];
      }

      // Update or add each new answer
      previousAnswers.forEach((newAnswer: any) => {
        const answerName = newAnswer.name || newAnswer.questionId;
        const normalizedAnswer = { ...newAnswer, name: answerName };

        const existingIndex = state.surveyAnswersForAllPreviousSurveys?.findIndex(
          (answerInState) => answerInState.name === answerName
        );

        if (existingIndex !== -1 && existingIndex !== undefined) {
          state.surveyAnswersForAllPreviousSurveys[
            existingIndex
          ] = normalizedAnswer;
        } else {
          state.surveyAnswersForAllPreviousSurveys.push(normalizedAnswer);
        }
      });
    },
    setSurveyCurrentPage(
      state,
      {
        payload
      }: PayloadAction<{
        surveyIdentifier: string;
        currentPageNumber: number;
      }>
    ) {
      if (!state[payload.surveyIdentifier]) {
        state[payload.surveyIdentifier] = {};
      }
      state[payload.surveyIdentifier]["currentPage"] =
        payload.currentPageNumber;
    },
    reset: (state) => (state = initialState)
  }
});

export const {
  setSurveyDataForSpecificSurvey,
  updateSurveyAnswersForAllPreviousSurveys,
  setSurveyCurrentPage,
  reset
} = SurveySlice.actions;

export const selectSurveyInfo = (state: { survey: SurveyState }) => {
  return state.survey;
};

export const selectAllPreviousSurveyAnswers = (state: {
  survey: SurveyState;
}): GlDecoratedSurveyAnswer[] | undefined => {
  return state.survey.surveyAnswersForAllPreviousSurveys || undefined;
};

export default SurveySlice.reducer;
